import { useEffect, useState } from 'react';
import * as Sentry from '@sentry/react';
import { authClient } from '../../utils/amplifyClient';
import { Discrepancy } from '../../types/schema';
import { getReport as getReportQuery } from '../../graphql/queries';
import { updateDiscrepancy as updateDiscrepancyMutation, updateReport as updateReportMutation } from '../../graphql/mutations';
import { useUserContext } from '../../contexts/UserContext';
import { ITEM_STATUS_CODES, ITEM_STATUS_CONTENT } from '../../utils/constants';
import { appendStatusChangeLog, resolveAndArchiveDiscrepancy, syncLinkedReportPromptStatus } from '../../utils/issueResolveArchive';
import { archiveDiscrepancyRecord } from '../../utils/issueArchiveUtils';
import { useAppContext } from '../../contexts/AppContext';

const client = authClient;

const getStatusOptions = (status: string) => {
    switch (status) {
        case ITEM_STATUS_CODES.inop:
            return [
                { value: '', label: ITEM_STATUS_CONTENT.no_change },
                { value: ITEM_STATUS_CODES.under_review, label: `Mark as ${ITEM_STATUS_CONTENT.under_review}` },
                { value: ITEM_STATUS_CODES.warn, label: `Mark as ${ITEM_STATUS_CONTENT.warn}` },
                { value: ITEM_STATUS_CODES.ok, label: `Mark as ${ITEM_STATUS_CONTENT.resolved}` },
            ];
        case ITEM_STATUS_CODES.warn:
            return [
                { value: '', label: ITEM_STATUS_CONTENT.no_change },
                { value: ITEM_STATUS_CODES.under_review, label: `Mark as ${ITEM_STATUS_CONTENT.under_review}` },
                { value: ITEM_STATUS_CODES.ok, label: `Mark as ${ITEM_STATUS_CONTENT.resolved}` },
            ];
        case ITEM_STATUS_CODES.under_review:
            return [
                { value: '', label: ITEM_STATUS_CONTENT.no_change },
                { value: ITEM_STATUS_CODES.warn, label: `Mark as ${ITEM_STATUS_CONTENT.warn}` },
                { value: ITEM_STATUS_CODES.inop, label: `Mark as ${ITEM_STATUS_CONTENT.inop}` },
                { value: ITEM_STATUS_CODES.ok, label: `Mark as ${ITEM_STATUS_CONTENT.resolved}` },
            ];
        default:
            return [
                { value: '', label: ITEM_STATUS_CONTENT.no_change },
                { value: ITEM_STATUS_CODES.ok, label: `Mark as ${ITEM_STATUS_CONTENT.resolved}` },
            ];
    }
};

interface IssueUpdateStatusModalProps {
    discrepancy: Discrepancy | null;
    onClose: () => void;
    onUpdated?: () => void;
}

export const IssueUpdateStatusModal = ({ discrepancy, onClose, onUpdated }: IssueUpdateStatusModalProps) => {
    const { companyUser, companyOwner } = useUserContext();
    const { setIssueCount } = useAppContext();
    const [updateStatus, setUpdateStatus] = useState<string | null>(null);
    const [updateNotes, setUpdateNotes] = useState('');
    const [isUpdating, setIsUpdating] = useState(false);

    useEffect(() => {
        if (!discrepancy) {
            setUpdateStatus(null);
            setUpdateNotes('');
        }
    }, [discrepancy]);

    useEffect(() => {
        if (!discrepancy) {
            return;
        }

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape' && !isUpdating) {
                onClose();
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [discrepancy, isUpdating, onClose]);

    if (!discrepancy) {
        return null;
    }

    const modalTitleId = `issue-update-modal-title-${discrepancy.id}`;
    const notesFieldId = `issue-update-notes-${discrepancy.id}`;

    const handleUpdate = async () => {
        setIsUpdating(true);

        const nextStatus = updateStatus || discrepancy.status;
        const nextLog = JSON.stringify([
            ...JSON.parse(discrepancy.log || '[]'),
            {
                name: companyUser?.name || companyOwner?.name,
                notes: `${updateNotes}${
                    updateStatus
                        ? ` [Updated Status: ${
                              ITEM_STATUS_CONTENT[updateStatus as keyof typeof ITEM_STATUS_CONTENT]
                          }] `
                        : ''
                }`,
                date: new Date().toISOString(),
            },
        ]);

        try {
            // Resolving archives with the final status/log — skip the intermediate update.
            if (updateStatus !== ITEM_STATUS_CODES.ok) {
                await client.graphql({
                    query: updateDiscrepancyMutation,
                    variables: {
                        input: {
                            id: discrepancy.id,
                            status: nextStatus,
                            log: nextLog,
                        },
                    },
                });
            }

            if (
                updateStatus &&
                updateStatus !== ITEM_STATUS_CODES.ok &&
                discrepancy.promptTitle &&
                discrepancy.promptLabel &&
                discrepancy.promptDescription
            ) {
                const reportResult = await client.graphql({
                    query: getReportQuery,
                    variables: {
                        id: discrepancy.reportId ?? '',
                    },
                });

                if (reportResult.data.getReport) {
                    const report = reportResult.data.getReport;
                    const userData = JSON.parse(report.userData || '[]');

                    const updatedUserData = userData.map((item: { title?: string; label?: string; description?: string; value?: string }) => {
                        if (
                            item.title === discrepancy.promptTitle &&
                            item.label === discrepancy.promptLabel &&
                            item.description === discrepancy.promptDescription
                        ) {
                            if (updateStatus === ITEM_STATUS_CODES.ok) {
                                return {
                                    ...item,
                                    value: updateStatus,
                                };
                            }
                            return item;
                        }
                        return item;
                    });

                    await client.graphql({
                        query: updateReportMutation,
                        variables: {
                            input: {
                                id: report.id,
                                userData: JSON.stringify(updatedUserData),
                            },
                        },
                    });
                }
            }

            if (updateStatus === ITEM_STATUS_CODES.ok) {
                const actorName = companyUser?.name || companyOwner?.name || 'Unknown';
                if (updateNotes.trim()) {
                    await resolveAndArchiveDiscrepancy(discrepancy, updateNotes, actorName);
                } else {
                    const nextLog = appendStatusChangeLog(
                        discrepancy,
                        actorName,
                        updateNotes,
                        ITEM_STATUS_CODES.ok,
                    );
                    await syncLinkedReportPromptStatus(discrepancy, ITEM_STATUS_CODES.ok);
                    await archiveDiscrepancyRecord({
                        ...discrepancy,
                        status: ITEM_STATUS_CODES.ok,
                        log: nextLog,
                    });
                }
                setIssueCount((prev) => Math.max(0, prev - 1));
            }

            setUpdateNotes('');
            setUpdateStatus(null);
            onUpdated?.();
            onClose();
        } catch (error) {
            console.error(error);
            Sentry.captureException(error);
        } finally {
            setIsUpdating(false);
        }
    };

    return (
        <div className="issues-modal-overlay" onClick={onClose}>
            <div
                className="issues-modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby={modalTitleId}
                onClick={(e) => e.stopPropagation()}
            >
                <div className="issues-modal-header">
                    <h2 id={modalTitleId}>Update {discrepancy.equipmentName}</h2>
                </div>
                <div className="issues-modal-body">
                    <div className="issues-radio-group">
                        {getStatusOptions(discrepancy.status).map((opt) => (
                            <div className="issues-radio-option" key={opt.value}>
                                <input
                                    type="radio"
                                    id={`issue-status-${discrepancy.id}-${opt.value}`}
                                    name={`issue-status-${discrepancy.id}`}
                                    value={opt.value}
                                    checked={(updateStatus || '') === opt.value}
                                    onChange={(e) => setUpdateStatus(e.target.value.length ? e.target.value : null)}
                                />
                                <label htmlFor={`issue-status-${discrepancy.id}-${opt.value}`}>{opt.label}</label>
                            </div>
                        ))}
                    </div>
                    <label className="issues-field-label" htmlFor={notesFieldId}>
                        Notes
                    </label>
                    <textarea
                        id={notesFieldId}
                        className="issues-textarea"
                        placeholder="Add a note"
                        value={updateNotes}
                        onChange={(e) => setUpdateNotes(e.target.value)}
                    />
                </div>
                <div className="issues-modal-footer">
                    <button className="btn-modal-cancel" onClick={onClose} disabled={isUpdating}>
                        Cancel
                    </button>
                    <button className="btn-modal-primary" onClick={handleUpdate} disabled={isUpdating}>
                        Update
                    </button>
                </div>
            </div>
        </div>
    );
};
